import React from 'react';
import Box from '@mui/material/Box';
import SyncRoundedIcon from '@mui/icons-material/SyncRounded';
import { useTranslation } from 'react-i18next';

import { dsV2 } from '@bloomays-lib/ui.shared';
import type { IContact, ContactSide } from '@bloomays-lib/types.shared';

import { resolveContactGroup, type ContactSlot } from '../../../helpers/missionDashboardV2';
// Type-only import depuis le module de définition (pas le barrel ./actions) pour éviter tout cycle.
import type { ContactSource } from './actions/ContactEditDrawer';
import { SrcBadge } from './badges';
import { GhostButton } from './primitives';
import { ContactRow } from './contacts';

export type ContactGroupHandlers = {
  onAdd?: (slot: ContactSlot, societyId?: string, societyName?: string) => void;
  onEdit?: (
    contact: IContact,
    slot: ContactSlot,
    source: ContactSource,
    societyId?: string,
    societyName?: string,
  ) => void;
  onManageAll?: (side: ContactSide) => void;
};

/** Groupe de contacts résolu (mission prioritaire / société fallback) — libellé flush à gauche. */
export const ContactGroup = ({
  label,
  slot,
  missionContacts,
  societyContacts,
  emptyHint,
  societyId,
  societyName,
  onAdd,
  onEdit,
  onManageAll,
}: {
  label: string;
  slot: ContactSlot;
  missionContacts?: (IContact | null | undefined)[];
  societyContacts?: (IContact | null | undefined)[];
  emptyHint: string;
  societyId?: string;
  societyName?: string;
} & ContactGroupHandlers) => {
  const { t } = useTranslation(['missionDashboard']);
  const resolved = resolveContactGroup<IContact>({ missionContacts, societyContacts });
  const editSource: ContactSource = resolved.source === 'society' ? 'society' : 'mission';
  return (
    <Box sx={{ borderBottom: `1px solid ${dsV2.color.borderLight}`, '&:last-of-type': { borderBottom: 'none' } }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          padding: '8px 18px',
          background: resolved.source === 'society' ? dsV2.color.amberSoft : dsV2.color.surface2,
          borderBottom: `1px solid ${resolved.source === 'society' ? dsV2.color.amberSoftBrd : dsV2.color.borderLight}`,
        }}
      >
        <Box
          sx={{
            fontSize: 10,
            fontWeight: 700,
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            color: dsV2.color.t3,
            textAlign: 'left',
          }}
        >
          {label}
        </Box>
        <Box sx={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '6px' }}>
          <SrcBadge source={resolved.source} />
          {onAdd ? (
            <GhostButton onClick={() => onAdd(slot, societyId, societyName)}>
              {slot.single && resolved.contacts.length > 0
                ? t('actions.replace', { ns: 'missionDashboard' })
                : t('actions.add', { ns: 'missionDashboard' })}
            </GhostButton>
          ) : null}
        </Box>
      </Box>
      <Box sx={{ padding: '0 18px' }}>
        {resolved.contacts.length > 0 ? (
          resolved.contacts.map((contact, index) => (
            <ContactRow
              key={contact.recordId ?? `${label}-${index}`}
              contact={contact}
              onEdit={onEdit ? () => onEdit(contact, slot, editSource, societyId, societyName) : undefined}
            />
          ))
        ) : (
          <Box sx={{ fontSize: '11.5px', color: dsV2.color.t4, fontStyle: 'italic', padding: '10px 0' }}>
            {emptyHint}
          </Box>
        )}
      </Box>
      {/* Note de repli (créa fb-note) — n'apparaît que si le groupe est résolu au niveau société. */}
      {resolved.source === 'society' ? (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: '8px',
            padding: '8px 18px 10px',
            fontSize: 11,
            lineHeight: 1.4,
            color: dsV2.color.amberDark,
            background: dsV2.color.amberSoft,
          }}
        >
          <SyncRoundedIcon sx={{ fontSize: 13, marginTop: '1px', flexShrink: 0 }} />
          <Box>{t('contacts.fallbackNote', { ns: 'missionDashboard' })}</Box>
          {onManageAll ? (
            <Box
              component="button"
              type="button"
              onClick={() => onManageAll(slot.side)}
              sx={{
                marginLeft: 'auto',
                whiteSpace: 'nowrap',
                fontWeight: 600,
                color: dsV2.color.amberDark,
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                fontSize: 11,
              }}
            >
              {t('contacts.manage', { ns: 'missionDashboard' })}
            </Box>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );
};
